/**
 * Vendored from `tamoomeh-service/packages/contracts/src/errors.ts`.
 *
 * The Tamoomeh Core API validates a customer request against the same catalog this
 * widget renders, so the two must agree. That copy is canonical; this one is a
 * byte-for-byte copy with only this header added, and `npm run check:contracts`
 * fails when they diverge. Edit Tamoomeh's copy and re-copy, never this file.
 */

/**
 * Consistent error contract shared by every Tamoomeh service.
 *
 * Every non-2xx response body is exactly:
 *
 *   { "error": { "code": "...", "message": "...", "details": ..., "request_id": "..." } }
 *
 * `code` is stable and machine-readable; `message` is human-readable and safe
 * to show. Never put secrets, OTP codes, tokens or provider credentials in
 * `message` or `details`.
 */

export const ERROR_CODES = {
  VALIDATION_ERROR: 400,
  MALFORMED_REQUEST: 400,
  UNAUTHENTICATED: 401,
  INVALID_CREDENTIALS: 401,
  TOKEN_EXPIRED: 401,
  TOKEN_INVALID: 401,
  FORBIDDEN: 403,
  SERVICE_SCOPE_VIOLATION: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  CONFLICT: 409,
  IDEMPOTENCY_CONFLICT: 409,
  RATE_LIMITED: 429,
  OTP_EXPIRED: 400,
  OTP_INVALID: 400,
  OTP_ATTEMPTS_EXCEEDED: 429,
  OTP_COOLDOWN: 429,
  PAYMENT_INVALID_STATE: 409,
  REQUEST_INVALID_STATE: 409,
  PAYMENT_AMOUNT_MISMATCH: 409,
  PAYMENT_VERIFICATION_FAILED: 502,
  PAYMENT_PROVIDER_UNAVAILABLE: 503,
  SMS_DELIVERY_UNAVAILABLE: 503,
  MANUAL_PAYMENT_ALREADY_REVIEWED: 409,
  UPLOAD_REJECTED: 400,
  UNSUPPORTED_MEDIA_TYPE: 415,
  PAYLOAD_TOO_LARGE: 413,
  UPSTREAM_ERROR: 502,
  INTERNAL_ERROR: 500,
  NOT_IMPLEMENTED: 501,
} as const;

export type ErrorCode = keyof typeof ERROR_CODES;

export interface ApiErrorBody {
  error: {
    code: ErrorCode;
    message: string;
    details?: unknown;
    request_id?: string;
  };
}

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details?: unknown;
  /** When true the message is considered safe to expose to the caller. */
  readonly expose = true;

  constructor(code: ErrorCode, message?: string, details?: unknown) {
    super(message ?? defaultMessage(code));
    this.name = 'ApiError';
    this.code = code;
    this.status = ERROR_CODES[code];
    this.details = details;
  }

  toBody(requestId?: string): ApiErrorBody {
    return {
      error: {
        code: this.code,
        message: this.message,
        ...(this.details === undefined ? {} : { details: this.details }),
        ...(requestId ? { request_id: requestId } : {}),
      },
    };
  }
}

const DEFAULT_MESSAGES: Record<ErrorCode, string> = {
  VALIDATION_ERROR: 'The request payload failed validation.',
  MALFORMED_REQUEST: 'The request could not be parsed.',
  UNAUTHENTICATED: 'Authentication is required.',
  INVALID_CREDENTIALS: 'The supplied credentials are not valid.',
  TOKEN_EXPIRED: 'The access token has expired.',
  TOKEN_INVALID: 'The access token is not valid.',
  FORBIDDEN: 'You are not allowed to perform this action.',
  SERVICE_SCOPE_VIOLATION: 'This administrator is not scoped to that service.',
  NOT_FOUND: 'The requested resource was not found.',
  METHOD_NOT_ALLOWED: 'That HTTP method is not allowed for this resource.',
  CONFLICT: 'The resource already exists or conflicts with the current state.',
  IDEMPOTENCY_CONFLICT: 'The idempotency key was reused with a different payload.',
  RATE_LIMITED: 'Too many requests. Please try again later.',
  OTP_EXPIRED: 'The verification code has expired. Request a new one.',
  OTP_INVALID: 'The verification code is incorrect.',
  OTP_ATTEMPTS_EXCEEDED: 'Too many incorrect attempts. Request a new code.',
  OTP_COOLDOWN: 'A code was requested recently. Please wait before requesting another.',
  PAYMENT_INVALID_STATE: 'The payment is not in a state that allows this operation.',
  REQUEST_INVALID_STATE: 'The request is not in a state that allows this operation.',
  PAYMENT_AMOUNT_MISMATCH: 'The payment amount does not match the authoritative amount.',
  PAYMENT_VERIFICATION_FAILED: 'The payment gateway did not confirm this payment.',
  PAYMENT_PROVIDER_UNAVAILABLE: 'The payment gateway is currently unavailable.',
  SMS_DELIVERY_UNAVAILABLE: 'The SMS could not be sent.',
  MANUAL_PAYMENT_ALREADY_REVIEWED: 'This manual payment submission has already been reviewed.',
  UPLOAD_REJECTED: 'The uploaded file was rejected.',
  UNSUPPORTED_MEDIA_TYPE: 'That file type is not accepted.',
  PAYLOAD_TOO_LARGE: 'The uploaded file is too large.',
  UPSTREAM_ERROR: 'An upstream service failed to respond correctly.',
  INTERNAL_ERROR: 'An unexpected error occurred.',
  NOT_IMPLEMENTED: 'This capability is not implemented.',
};

/**
 * What a customer sees when the verification SMS cannot be sent.
 *
 * Shared by Core, which answers `SMS_DELIVERY_UNAVAILABLE` with exactly this
 * message, and by the widget, which shows exactly this for that code whatever
 * the body says. The provider's own reason stays in Core's log.
 */
export const SMS_DELIVERY_UNAVAILABLE_MESSAGE_FA = 'ارسال پیامک در حال حاضر امکان پذیر نیست';

/**
 * What a customer sees when the phone number they asked a code for is not a
 * valid Iranian mobile number, in place of the generic validation message.
 * Shown only once they ask for the code, never while they are typing.
 */
export const INVALID_PHONE_MESSAGE_FA = 'لطفا شماره تلفن را به صورت صحیح وارد کنید';

export function defaultMessage(code: ErrorCode): string {
  return DEFAULT_MESSAGES[code];
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}
